import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/Authcontext/AuthContext";
import { completeAccountSetup } from "../Functions/firebaseFunction";
import LoadingScreen from "../components/LoadingScreen";
import { 
  Building2, 
  Phone, 
  MapPin, 
  Trash2, 
  Edit3, 
  Plus, 
  AlertCircle, 
  CheckCircle2, 
  ArrowRight, 
  ArrowLeft, 
  Camera, 
  Sparkles, 
  Check, 
  ShieldCheck, 
  Image as ImageIcon 
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const AccountSetup = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [stepError, setStepError] = useState("");
  const [formData, setFormData] = useState({
    mobileNo: "",
    headquarters: "",
    city: "",
    state: "",
    zip: "",
    logo: null,
    profilePicture: null,
    additionalImages: [],
    logoPreview: null,
    profilePreview: null,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();

  // Input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
    if (stepError) setStepError("");
  };

  // Single file uploads (logo / profile)
  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError(`${type === "logo" ? "Logo" : "Profile photo"} must be under 5MB.`);
        return;
      }
      if (!file.type.startsWith("image/")) {
        setError("Please select a valid image file (PNG, JPG, WEBP).");
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setFormData((prev) => ({
        ...prev,
        [type === "logo" ? "logo" : "profilePicture"]: file,
        [type === "logo" ? "logoPreview" : "profilePreview"]: previewUrl,
      }));

      if (error) setError("");
      if (stepError) setStepError("");
    }
    e.target.value = "";
  };

  // Additional facility photos
  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      for (let file of files) {
        if (file.size > 5 * 1024 * 1024) {
          setError("Each workshop photo must be under 5MB.");
          return;
        }
        if (!file.type.startsWith("image/")) {
          setError("Please upload only image files.");
          return;
        }
      }

      const newImages = files.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        id: Date.now() + Math.random(),
      }));

      setFormData((prev) => ({
        ...prev,
        additionalImages: [...prev.additionalImages, ...newImages],
      }));

      if (error) setError("");
      if (stepError) setStepError("");
    }
  };

  // Remove facility photo
  const removeAdditionalImage = (imageId) => {
    setFormData((prev) => {
      const img = prev.additionalImages.find((item) => item.id === imageId);
      if (img?.preview) URL.revokeObjectURL(img.preview);
      return {
        ...prev,
        additionalImages: prev.additionalImages.filter((item) => item.id !== imageId),
      };
    });
  };

  // Edit file trigger
  const handleEditImage = (type) => {
    const inputId = type === "logo" ? "upload-logo" : "upload-profilePicture";
    document.getElementById(inputId)?.click();
  };

  // Delete single image
  const handleDeleteImage = (type) => {
    if (type === "logo") {
      if (formData.logoPreview) URL.revokeObjectURL(formData.logoPreview);
      setFormData((prev) => ({ ...prev, logo: null, logoPreview: null }));
    } else {
      if (formData.profilePreview) URL.revokeObjectURL(formData.profilePreview);
      setFormData((prev) => ({ ...prev, profilePicture: null, profilePreview: null }));
    }
  };

  // Step 1 Validation
  const validateStep1 = () => {
    if (!formData.mobileNo?.trim()) {
      setStepError("Please provide your primary business phone number.");
      return false;
    }
    if (!formData.headquarters?.trim()) {
      setStepError("Please enter your workshop street address.");
      return false;
    }
    if (!formData.city?.trim() || !formData.state?.trim() || !formData.zip?.trim()) {
      setStepError("Please fill in City, State, and ZIP code.");
      return false;
    }
    setStepError("");
    return true;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (currentStep === 1) {
      if (validateStep1()) setCurrentStep(2);
    } else if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  const handlePrev = () => {
    setStepError("");
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  // Submit complete onboarding
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.mobileNo.trim() || !formData.headquarters.trim()) {
      setStepError("Please fill in your workshop address and phone number.");
      setCurrentStep(1);
      return;
    }
    if (formData.additionalImages.length < 3) {
      setStepError("Please upload at least 3 workshop / bay photos before completing setup.");
      return;
    }
    if (!currentUser) {
      setError("Session expired. Please sign in again.");
      return;
    }

    try {
      setError("");
      setStepError("");
      setUploadProgress("Uploading business assets...");
      await completeAccountSetup(
        formData,
        currentUser,
        setError,
        setLoading,
        navigate,
        setUploadProgress
      );
    } catch (err) {
      console.error(err);
      setError("An unexpected error occurred during setup. Please try again.");
    } finally {
      setUploadProgress("");
    }
  };

  if (authLoading) {
    return <LoadingScreen message="Setting up your account..." />;
  }

  const additionalCount = formData.additionalImages?.length || 0;
  const isAdditionalValid = additionalCount >= 3;
  const isStep1Done = Boolean(formData.mobileNo && formData.headquarters && formData.city && formData.state && formData.zip);
  const isStep2Done = Boolean(formData.logoPreview || formData.profilePreview);

  const stepsMeta = [
    { num: 1, title: "Location", headline: "Where is your workshop?", sub: "Enter your physical facility address for nearby driver bookings." },
    { num: 2, title: "Branding", headline: "Upload brand identity", sub: "Upload your shop logo and owner photo to build instant customer trust." },
    { num: 3, title: "Facility Photos", headline: "Showcase your bays", sub: "Upload at least 3 photos of your service bays, diagnostic tools, and waiting area." },
  ];

  return (
    <div className="h-screen w-screen flex overflow-hidden bg-white">

      {/* ── LEFT: Brand Panel with 3 Animated Milestone Cards ── */}
      <div className="hidden lg:flex w-[42%] h-full flex-col justify-between relative overflow-hidden bg-[#EEF5FF] px-12 py-12">
        
        {/* Soft background glows */}
        <div className="absolute top-[-80px] right-[-80px] w-[360px] h-[360px] rounded-full bg-[#007AFF]/10 blur-[90px] pointer-events-none" />
        <div className="absolute bottom-[-60px] left-[-60px] w-[280px] h-[280px] rounded-full bg-indigo-200/40 blur-[70px] pointer-events-none" />

        {/* Subtle dot grid */}
        <div
          className="absolute inset-0 opacity-[0.3] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle, #93c5fd 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />

        {/* Top Logo & Step Badge */}
        <div className="relative z-10 flex items-center justify-between">
          <Link to="/">
            <img
              src="/logo.png"
              alt="Americar"
              className="h-9 w-auto object-contain"
            />
          </Link>
          <span className="text-[11px] font-bold text-[#007AFF] bg-white/80 border border-blue-100 px-3 py-1 rounded-full backdrop-blur-xs">
            Step {currentStep} of 3
          </span>
        </div>

        {/* ── 3 Animated Milestone Cards (Clean, Linear & Apple style) ── */}
        <div className="relative z-10 flex flex-col items-center justify-center gap-3.5 flex-1 py-8">

          {/* Milestone Card 1: Workshop Location */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: [0, -6, 0] }}
            transition={{ opacity: { duration: 0.4 }, y: { duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0 } }}
            className={`w-full max-w-[310px] bg-white rounded-2xl p-4 border transition-all shadow-[0_4px_24px_rgba(0,122,255,0.06)] ${
              currentStep === 1
                ? "border-[#007AFF]/40 ring-2 ring-[#007AFF]/10"
                : "border-white/80"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                  isStep1Done ? "bg-emerald-50 text-emerald-600" : "bg-blue-50 text-[#007AFF]"
                }`}>
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-800">Workshop Location</h4>
                  <p className="text-[10px] text-slate-400">Dispatch & customer radius</p>
                </div>
              </div>
              {isStep1Done ? (
                <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                  <Check className="w-3 h-3 stroke-[3]" /> Set
                </span>
              ) : (
                <span className="text-[10px] font-semibold text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">
                  Step 1
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 bg-slate-50 rounded-xl px-2.5 py-1.5 truncate font-medium">
              {formData.headquarters ? `${formData.headquarters}${formData.city ? `, ${formData.city}` : ""}` : "123 Industrial Way, Suite A"}
            </div>
          </motion.div>

          {/* Milestone Card 2: Brand Identity */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: [0, -6, 0] }}
            transition={{ opacity: { duration: 0.4, delay: 0.15 }, y: { duration: 4.4, repeat: Infinity, ease: "easeInOut", delay: 0.8 } }}
            className={`w-full max-w-[310px] bg-white rounded-2xl p-4 border transition-all self-end shadow-[0_4px_24px_rgba(0,122,255,0.06)] ${
              currentStep === 2
                ? "border-[#007AFF]/40 ring-2 ring-[#007AFF]/10"
                : "border-white/80"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                  formData.logoPreview || formData.profilePreview ? "bg-emerald-50 text-emerald-600" : "bg-violet-50 text-violet-500"
                }`}>
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-800">Brand Identity</h4>
                  <p className="text-[10px] text-slate-400">Search cards & invoices</p>
                </div>
              </div>
              {formData.logoPreview || formData.profilePreview ? (
                <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                  <Check className="w-3 h-3 stroke-[3]" /> Set
                </span>
              ) : (
                <span className="text-[10px] font-semibold text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">
                  Step 2
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <div className="w-7 h-7 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center shrink-0 border border-slate-200">
                {formData.logoPreview ? (
                  <img src={formData.logoPreview} alt="Logo" className="w-full h-full object-cover" />
                ) : formData.profilePreview ? (
                  <img src={formData.profilePreview} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-3.5 h-3.5 text-slate-400" />
                )}
              </div>
              <span className="text-[11px] text-slate-500 truncate font-medium">
                {formData.logo
                  ? formData.logo.name
                  : formData.profilePicture
                  ? formData.profilePicture.name
                  : "Company Logo & Photo"}
              </span>
            </div>
          </motion.div>

          {/* Milestone Card 3: Facility Photos */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: [0, -6, 0] }}
            transition={{ opacity: { duration: 0.4, delay: 0.3 }, y: { duration: 3.8, repeat: Infinity, ease: "easeInOut", delay: 1.6 } }}
            className={`w-full max-w-[310px] bg-white rounded-2xl p-4 border transition-all self-start shadow-[0_4px_24px_rgba(0,122,255,0.06)] ${
              currentStep === 3
                ? "border-[#007AFF]/40 ring-2 ring-[#007AFF]/10"
                : "border-white/80"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                  isAdditionalValid ? "bg-emerald-50 text-emerald-600" : "bg-blue-50 text-[#007AFF]"
                }`}>
                  <Camera className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-800">Workshop Photos</h4>
                  <p className="text-[10px] text-slate-400">Bays, tools & reception</p>
                </div>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                isAdditionalValid
                  ? "text-emerald-600 bg-emerald-50"
                  : "text-slate-500 bg-slate-50"
              }`}>
                {additionalCount}/3 photos
              </span>
            </div>
            <div className="flex gap-1.5 pt-1">
              {[0, 1, 2].map((idx) => (
                <div
                  key={idx}
                  className="flex-1 h-9 rounded-lg bg-slate-50 border border-slate-100 overflow-hidden flex items-center justify-center"
                >
                  {formData.additionalImages[idx] ? (
                    <img
                      src={formData.additionalImages[idx].preview}
                      alt="Thumbnail"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-[9px] text-slate-300 font-bold">Bay {idx + 1}</span>
                  )}
                </div>
              ))}
            </div>
          </motion.div>

        </div>

        {/* Bottom Headline & Subtitle */}
        <div className="relative z-10">
          <span className="inline-block text-[#007AFF] text-[10px] font-bold tracking-[0.15em] uppercase mb-2 bg-white/70 border border-blue-100 px-2.5 py-0.5 rounded-full">
            Partner Onboarding
          </span>
          <h2 className="text-slate-900 text-2xl font-bold tracking-tight leading-[1.25] mb-1">
            Build your shop profile.
          </h2>
          <p className="text-slate-400 text-xs leading-relaxed max-w-sm">
            Set up your details once to start receiving local customer requests.
          </p>
        </div>

      </div>

      {/* ── RIGHT: Focused Enterprise Stepper (Apple & Stripe style) ── */}
      <div className="flex-1 h-full overflow-y-auto bg-white flex flex-col items-center px-6 sm:px-10 lg:px-12 xl:px-16 py-10">
        <div className="w-full max-w-[560px] my-auto">

          {/* Mobile Logo & Skip Link */}
          <div className="flex items-center justify-between mb-8">
            <div className="lg:hidden">
              <Link to="/">
                <img src="/logo.png" alt="Americar" className="h-8 w-auto object-contain" />
              </Link>
            </div>
            <Link
              to="/dashboard"
              onClick={(e) => loading && e.preventDefault()}
              className={`text-xs font-semibold text-slate-400 transition-colors ml-auto flex items-center gap-1 ${
                loading ? "pointer-events-none opacity-40 cursor-not-allowed" : "hover:text-[#007AFF]"
              }`}
            >
              Skip to Dashboard →
            </Link>
          </div>

          {/* Minimal Step Indicator */}
          <div className="mb-7">
            <div className="flex items-center justify-between text-xs mb-2.5">
              <span className="font-semibold text-slate-900 tracking-tight flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#007AFF]" />
                Step {currentStep} of 3
                <span className="text-slate-400 font-normal">· {stepsMeta[currentStep - 1].title}</span>
              </span>
              <span className="text-[11px] font-medium text-slate-400">
                {currentStep === 1 ? "Next: Branding" : currentStep === 2 ? "Next: Photos" : "Final Step"}
              </span>
            </div>

            {/* Clean Segmented Track */}
            <div className="grid grid-cols-3 gap-2">
              {stepsMeta.map((s) => {
                const isCompleted = s.num < currentStep;
                const isCurrent = s.num === currentStep;
                return (
                  <button
                    key={s.num}
                    type="button"
                    disabled={loading || s.num >= currentStep}
                    onClick={() => {
                      if (!loading && s.num < currentStep) setCurrentStep(s.num);
                    }}
                    className={`h-1 rounded-full transition-all duration-300 ${
                      s.num <= currentStep ? "bg-[#007AFF]" : "bg-slate-100"
                    } ${!loading && isCompleted ? "hover:opacity-80 cursor-pointer" : "cursor-default"}`}
                    aria-label={`Step ${s.num}: ${s.title}`}
                  />
                );
              })}
            </div>
          </div>

          {/* Screen Title & Subtitle */}
          <div className="mb-7">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-1.5">
              {stepsMeta[currentStep - 1].headline}
            </h1>
            <p className="text-slate-500 text-sm">
              {stepsMeta[currentStep - 1].sub}
            </p>
          </div>

          {/* Error Message */}
          {(stepError || error) && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs sm:text-sm font-medium flex items-center gap-2 mb-6"
            >
              <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
              <span>{stepError || error}</span>
            </motion.div>
          )}

          {/* ── STEP CONTENT ── */}
          <form onSubmit={handleSubmit}>
            <AnimatePresence mode="wait">

              {/* ──────────────── STEP 1: LOCATION & CONTACT ──────────────── */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-5"
                >
                  {/* Street Address */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">
                      Workshop Street Address <span className="text-[#007AFF]">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                        <Building2 className="w-4 h-4" />
                      </div>
                      <input
                        type="text"
                        name="headquarters"
                        value={formData.headquarters}
                        onChange={handleInputChange}
                        placeholder="123 Industrial Way, Suite A"
                        className="w-full pl-11 pr-4 py-3.5 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-[#007AFF] focus:ring-4 focus:ring-[#007AFF]/10 transition-all font-medium text-slate-900 placeholder:text-slate-400 shadow-xs"
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* City & State (2 columns) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-800 mb-2">
                        City <span className="text-[#007AFF]">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                          <MapPin className="w-4 h-4" />
                        </div>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          placeholder="Los Angeles"
                          className="w-full pl-11 pr-4 py-3.5 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-[#007AFF] focus:ring-4 focus:ring-[#007AFF]/10 transition-all font-medium text-slate-900 placeholder:text-slate-400 shadow-xs"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-800 mb-2">
                        State <span className="text-[#007AFF]">*</span>
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="California"
                        className="w-full px-4 py-3.5 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-[#007AFF] focus:ring-4 focus:ring-[#007AFF]/10 transition-all font-medium text-slate-900 placeholder:text-slate-400 shadow-xs"
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* ZIP & Business Phone (2 columns) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-800 mb-2">
                        ZIP Code <span className="text-[#007AFF]">*</span>
                      </label>
                      <input
                        type="text"
                        name="zip"
                        value={formData.zip}
                        onChange={handleInputChange}
                        placeholder="90001"
                        className="w-full px-4 py-3.5 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-[#007AFF] focus:ring-4 focus:ring-[#007AFF]/10 transition-all font-medium text-slate-900 placeholder:text-slate-400 shadow-xs"
                        required
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-800 mb-2">
                        Primary Business Phone <span className="text-[#007AFF]">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                          <Phone className="w-4 h-4" />
                        </div>
                        <input
                          type="tel"
                          name="mobileNo"
                          value={formData.mobileNo}
                          onChange={handleInputChange}
                          placeholder="(555) 000-0000"
                          className="w-full pl-11 pr-4 py-3.5 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-[#007AFF] focus:ring-4 focus:ring-[#007AFF]/10 transition-all font-medium text-slate-900 placeholder:text-slate-400 shadow-xs"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Continue Button */}
                  <div className="pt-3">
                    <button
                      type="button"
                      onClick={handleNext}
                      disabled={loading}
                      className="w-full py-3.5 px-6 bg-[#007AFF] hover:bg-blue-600 active:scale-[0.99] text-white rounded-xl font-semibold text-sm shadow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none"
                    >
                      <span>Continue to Branding</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* ──────────────── STEP 2: BRAND IDENTITY ──────────────── */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {[
                      {
                        type: "logo",
                        previewKey: "logoPreview",
                        label: "Workshop Logo",
                        sub: "Search results & customer receipts",
                        icon: <Camera className="w-6 h-6 text-[#007AFF]" />,
                      },
                      {
                        type: "profilePicture",
                        previewKey: "profilePreview",
                        label: "Manager / Storefront",
                        sub: "Builds instant trust with drivers",
                        icon: <ImageIcon className="w-6 h-6 text-indigo-500" />,
                      },
                    ].map((item) => {
                      const hasPreview = !!formData[item.previewKey];
                      const previewUrl = formData[item.previewKey];

                      return (
                        <div key={item.type} className="flex flex-col">
                          <label className="block text-sm font-semibold text-slate-800 mb-1">
                            {item.label}
                          </label>
                          <p className="text-xs text-slate-400 mb-2.5">{item.sub}</p>

                          <div
                            className={`w-full h-52 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col justify-center items-center overflow-hidden relative transition-all shadow-2xs ${
                              loading
                                ? "pointer-events-none opacity-50 cursor-not-allowed bg-slate-100"
                                : "hover:border-[#007AFF] bg-slate-50/60 hover:bg-blue-50/20 cursor-pointer group"
                            }`}
                            onClick={() =>
                              !loading && !hasPreview && document.getElementById(`upload-${item.type}`).click()
                            }
                          >
                            {hasPreview ? (
                              <>
                                <img
                                  src={previewUrl}
                                  alt={`${item.label} preview`}
                                  className="w-full h-full object-cover"
                                />
                                <div className={`absolute inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center gap-2 transition-opacity ${
                                  loading ? "pointer-events-none opacity-0" : "opacity-0 group-hover:opacity-100"
                                }`}>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!loading) handleEditImage(item.type);
                                    }}
                                    className="px-3 py-2 bg-white text-slate-800 rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-sm hover:bg-slate-100 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                    disabled={loading}
                                  >
                                    <Edit3 className="w-3.5 h-3.5" /> Change
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!loading) handleDeleteImage(item.type);
                                    }}
                                    className="px-3 py-2 bg-red-600 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-sm hover:bg-red-700 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                    disabled={loading}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" /> Remove
                                  </button>
                                </div>
                              </>
                            ) : (
                              <div className="text-center p-4">
                                <div className="w-12 h-12 rounded-2xl bg-white shadow-xs border border-slate-100 flex items-center justify-center mx-auto mb-2.5 text-slate-400 group-hover:text-[#007AFF] group-hover:scale-105 transition-all">
                                  {item.icon}
                                </div>
                                <span className="text-xs font-bold text-slate-700 block mb-0.5">
                                  Upload {item.label}
                                </span>
                                <span className="text-[11px] text-slate-400">PNG, JPG up to 5MB</span>
                              </div>
                            )}

                            <input
                              type="file"
                              id={`upload-${item.type}`}
                              onChange={(e) => handleFileChange(e, item.type)}
                              className="hidden"
                              accept="image/*"
                              disabled={loading}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Actions */}
                  <div className="pt-3 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handlePrev}
                      disabled={loading}
                      className="py-3.5 px-5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:pointer-events-none"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Back</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleNext}
                      disabled={loading}
                      className="flex-1 py-3.5 px-6 bg-[#007AFF] hover:bg-blue-600 active:scale-[0.99] text-white rounded-xl font-semibold text-sm shadow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none"
                    >
                      <span>Continue to Facility Photos</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* ──────────────── STEP 3: WORKSHOP PHOTOS ──────────────── */}
              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-5"
                >
                  <div className="flex items-center justify-between pb-1">
                    <span className="text-sm font-semibold text-slate-800">
                      Facility Gallery
                    </span>
                    <span
                      className={`text-[11px] font-bold px-3 py-1 rounded-full flex items-center gap-1.5 ${
                        isAdditionalValid
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                          : "bg-amber-50 text-amber-700 border border-amber-100"
                      }`}
                    >
                      {isAdditionalValid ? (
                        <>
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          <span>{additionalCount} photos uploaded (Requirement met)</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                          <span>{additionalCount} of 3 photos (min. 3 required)</span>
                        </>
                      )}
                    </span>
                  </div>

                  {/* Photo Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3.5">
                    {formData.additionalImages.map((image, idx) => (
                      <div
                        key={image.id}
                        className="relative group rounded-2xl overflow-hidden h-36 border border-slate-200 shadow-2xs"
                      >
                        <img
                          src={image.preview}
                          alt={`Facility ${idx + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <span className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-xs text-white text-[10px] font-bold px-2 py-0.5 rounded-md">
                          Photo {idx + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => !loading && removeAdditionalImage(image.id)}
                          className={`absolute top-2 right-2 bg-slate-900/70 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs transition-all shadow-sm ${
                            loading ? "pointer-events-none opacity-0" : "opacity-0 group-hover:opacity-100"
                          }`}
                          disabled={loading}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}

                    {/* Add Photo Tile */}
                    <div
                      className={`h-36 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col justify-center items-center p-4 text-center transition-all ${
                        loading
                          ? "pointer-events-none opacity-50 cursor-not-allowed bg-slate-100"
                          : "hover:border-[#007AFF] bg-slate-50/60 hover:bg-blue-50/20 cursor-pointer group"
                      }`}
                      onClick={() => !loading && document.getElementById("uploadAdditional").click()}
                    >
                      <div className="w-9 h-9 rounded-xl bg-white shadow-xs border border-slate-100 flex items-center justify-center mb-2 text-slate-400 group-hover:text-[#007AFF] group-hover:scale-105 transition-all">
                        <Plus className="w-5 h-5" />
                      </div>
                      <span className="text-xs font-bold text-slate-700 block mb-0.5">Add Photo</span>
                      <span className="text-[10px] text-slate-400">Max 5MB each</span>

                      <input
                        type="file"
                        id="uploadAdditional"
                        onChange={handleAdditionalImagesChange}
                        className="hidden"
                        accept="image/*"
                        multiple
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="pt-3 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handlePrev}
                      className="py-3.5 px-5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:pointer-events-none"
                      disabled={loading}
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Back</span>
                    </button>

                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 py-3.5 px-6 bg-[#007AFF] hover:bg-blue-600 active:scale-[0.99] text-white rounded-xl font-semibold text-sm shadow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:pointer-events-none"
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span>{uploadProgress || "Saving Profile..."}</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          <span>Complete & Open Dashboard</span>
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}

            </AnimatePresence>
          </form>

          {/* Secure Footer Note */}
          <div className="mt-8 pt-5 border-t border-slate-100 flex items-center justify-center text-xs text-slate-400 gap-1.5">
            <ShieldCheck className="w-4 h-4 text-emerald-500" /> Encrypted & verified by Americar Network
          </div>

        </div>
      </div>

    </div>
  );
};

export default AccountSetup;
